import { faPaperclip } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EmailMessageAttachment } from 'api/email-messages/types';
import { FC } from 'react';
import styled from 'styled-components';

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
  'bmp',
]);

const isImageAttachment = (name: string) => {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';

  return IMAGE_EXTENSIONS.has(ext);
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileCard = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  padding: var(--spacing-03) var(--spacing-04);
  background: var(--white-100);
  border: 1px solid var(--field-border-default);
  border-radius: var(--border-radius-default);
  cursor: pointer;
  width: 240px;
  text-align: left;
  transition: background 150ms ease;

  &:hover {
    background: var(--cool-gray-05, #f9f9f9);
    border-color: var(--cool-gray-30, #c6c6c6);
  }
`;

const FileIconWrapper = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: var(--border-radius-small);
  background: var(--blue-10, #edf5ff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--blue-60, #2563eb);
`;

const FileMeta = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const FileName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.span`
  font-size: 11px;
  color: var(--text-tertiary);
`;

const ImageCard = styled.button`
  display: inline-flex;
  flex-direction: column;
  gap: var(--spacing-02);
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
`;

const ImageThumb = styled.img`
  width: 160px;
  height: 108px;
  object-fit: cover;
  border-radius: var(--border-radius-default);
  border: 1px solid var(--field-border-default);
  display: block;
  transition: opacity 150ms ease;

  &:hover {
    opacity: 0.88;
  }
`;

const ImageName = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export type RenderEmailAttachment = (
  attachment: EmailMessageAttachment,
) => React.ReactNode;

interface Props {
  attachment: EmailMessageAttachment;
  renderAttachment?: RenderEmailAttachment;
}

export const EmailAttachment: FC<Props> = ({
  attachment,
  renderAttachment,
}) => {
  if (renderAttachment) {
    return <>{renderAttachment(attachment)}</>;
  }

  const openFile = () =>
    window.open(
      attachment.downloadUrl ?? attachment.url ?? attachment.location,
      '_blank',
      'noopener,noreferrer',
    );

  if (isImageAttachment(attachment.name)) {
    return (
      <ImageCard type="button" onClick={openFile} title={attachment.name}>
        <ImageThumb
          src={attachment.url ?? attachment.location}
          alt={attachment.name}
          loading="lazy"
        />
        <ImageName>{attachment.name}</ImageName>
      </ImageCard>
    );
  }

  return (
    <FileCard type="button" onClick={openFile} title={attachment.name}>
      <FileIconWrapper>
        <FontAwesomeIcon icon={faPaperclip} size="sm" />
      </FileIconWrapper>
      <FileMeta>
        <FileName>{attachment.name}</FileName>
        {attachment.sizeInBytes > 0 && (
          <FileSize>{formatBytes(attachment.sizeInBytes)}</FileSize>
        )}
      </FileMeta>
    </FileCard>
  );
};
